import { DOCUMENT, DatePipe } from '@angular/common';
import {
    Component,
    Input,
    ViewChild,
    ElementRef,
    ChangeDetectionStrategy,
    HostListener,
    OnDestroy,
    OnInit,
    Inject,
} from '@angular/core';
import { DateutilService } from 'app/shared/services/dateutil.service';
import { StatusCodeService } from 'app/shared/services/status-code.service';
import { StringUtils } from 'app/shared/utils/string-utils';
import * as Highcharts from 'highcharts';
import { Subscription } from 'rxjs';
import { ChartLegend, CustomLegendOptions } from '.';
const multicolorSeries = require('highcharts-multicolor-series/js/multicolor_series');
multicolorSeries(Highcharts);

@Component({
    selector: 'app-hydro-graph',
    templateUrl: './hydro-graph.component.html',
    styleUrls: ['./hydro-graph.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HydroGraphComponent implements OnInit, OnDestroy {
    @Input() public containerCssClass = 'graph-container';
    @Input() public chartId: string | number = 'chart-div-id';
    @Input() public chartTitle = '';
    @Input() public chartType = 'line';
    @Input() public chartLegend: ChartLegend = new ChartLegend();
    @Input() public showZoom = true;
    @Input() public showRangeSelector = false;
    @Input() public showCredits = false;
    @Input() public chartXAxis: Highcharts.XAxisOptions | Highcharts.XAxisOptions[];
    @Input() public chartYAxis: Highcharts.YAxisOptions | Highcharts.YAxisOptions[];
    @Input() public chartRangeSelectorOption: Highcharts.RangeSelectorOptions;
    @Input() public chartToolTipOption: Highcharts.TooltipOptions;
    @Input() public chartSeriesData: Highcharts.SeriesOptionsType[];
    @Input() public chartPlotOptions: Highcharts.PlotOptions;
    @Input() public showGraph: boolean;
    @Input() public enableExport = false;
    @Input() public showNavigator = true;
    @HostListener('window:resize', ['$event']) public onResize(event: Event) {
        const newWidth = this.getWidth() - 140;
        this.chartSWidth = newWidth;
        this.chartElement.setSize(newWidth);
        this.chartElement.reflow();
    }
    @HostListener('window:beforeprint', ['$event']) public onBeforePrint(event: Event) {
        // Printing takes a screenshot of the screen to render
        // Since highcharts is not straight SVG, you need to get the chart to pixel width
        // 850px is ideal for letter sized paper correct chart rendering
        this.document.body.classList.add('flow-balance-report-print-page-sizing');
        this.chartScreenWidth = this.chartElement.chartWidth;
        setTimeout(() => {
            this.chartElement.setSize(1000, 400, false);
            this.chartElement.reflow();
        }, 0);
    }
    @HostListener('window:afterprint', ['$event']) public onAfterPrint(event: Event) {
        // After printing the chart should be resized back to correct view size
        document.body.className = document.body.className.replace('flow-balance-report-print-page-sizing', '');
        setTimeout(() => {
            this.chartElement.setSize(this.chartSWidth);
            this.chartElement.reflow();
        }, 0);
    }

    @ViewChild('chart') public chartElementRef: ElementRef<HTMLDivElement>;
    public chartElement: Highcharts.Chart;
    @Input() public isLoading = false;
    @Input() public showCustomLegend = false;
    @Input() public chartCustomLegend: CustomLegendOptions;
    @Input() public chartScrollBarOptions: Highcharts.ScrollbarOptions;
    @Input() public chartHeightInPixel = 400;
    @Input() public chartEvents: Highcharts.ChartEventsOptions;
    @Input() public isDarkTheme = false;

    public chartOptions: Highcharts.Options;
    public xAxisExtreams: number[];
    private chartScreenWidth = 0;
    private chartSWidth = 0;
    public chartObject: Highcharts.Chart | null = null;

    private subscriptions: Array<Subscription> = [];
    public customDateFormat: string;

    constructor(
        private el: ElementRef,
        private statusCodeService: StatusCodeService,
        public dateutilService: DateutilService,
        @Inject(DOCUMENT) private document: Document,
        private datePipe: DatePipe
    ) { }

    public ngOnInit(): void {
        this.subscriptions.push(this.dateutilService.dateFormat.subscribe(() => {
            const dateFormat = this.dateutilService.getFormat();
            const is12HourFormat = this.dateutilService.timeFormat.getValue() !== 'hh:mm:ss';
            this.customDateFormat = is12HourFormat ? `${dateFormat}, ${'hh:mm a'}` : `${dateFormat}, ${'HH:mm'}`;
        }));

        const isDarkMode = this.statusCodeService.userInfoThemeBS.getValue();
        if (isDarkMode) {
            StringUtils.setHighChartTheme();
        } else {
            StringUtils.setHighchartWhiteTheme();
        }

        const themeSub = this.statusCodeService.userInfoThemeBS.subscribe((response: boolean) => {
            if (response) {
                StringUtils.setHighChartTheme(this.chartObject);
            } else {
                StringUtils.setHighchartWhiteTheme(this.chartObject);
            }
        });
        this.subscriptions.push(themeSub);
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.subscriptions = [];
    }

    // tslint:disable-next-line: cyclomatic-complexity
    public renderChart() {

        this.showGraph = !!this.chartSeriesData || false;
        const chartRenderId = !this.chartElementRef?.nativeElement
            ? this.chartOptions?.chart?.renderTo
            : this.chartElementRef.nativeElement;

        this.chartOptions = {
            chart: {
                type: this.chartType,
                ignoreHiddenSeries: false,
                zoomType: !!this.showZoom ? 'xy' : undefined,
                renderTo: chartRenderId,
                height: this.chartHeightInPixel || 400,
            },
            title: {
                text: !!this.chartTitle ? this.chartTitle : '',
            },
            exporting: {
                enabled: this.enableExport || false,
            },
            rangeSelector: {
                enabled: true,
                inputEnabled: false,
                buttons: [
                    {
                        type: 'day',
                        count: 1,
                        text: '1d',
                    },
                    {
                        type: 'week',
                        count: 1,
                        text: '1w',
                    },
                    {
                        type: 'month',
                        count: 1,
                        text: '1m',
                    },
                    {
                        type: 'all',
                        text: 'All',
                    },
                ],
                selected: 3,
            },
            navigator: {
                enabled: this.showNavigator || false,
            },
            tooltip: this.chartToolTipOption,
            credits: {
                enabled: this.showCredits,
            },
            series: this.chartSeriesData || [],
        };

        const that = this;
        // chart x-axis
        if (this.chartXAxis) {
            this.chartOptions.xAxis = this.chartXAxis;
            const xAxisArray = Array.isArray(this.chartOptions.xAxis) ? this.chartOptions.xAxis : [this.chartOptions.xAxis];
            if (xAxisArray[0]?.labels) {
                xAxisArray[0].labels.formatter = function (this: Highcharts.AxisLabelsFormatterContextObject) {
                    return that.datePipe.transform(this.value as number, that.customDateFormat) || '';
                };
            }
        }

        // chart y-axis
        if (this.chartYAxis) {
            this.chartOptions.yAxis = this.chartYAxis;
        }

        // chart plot options
        if (this.chartPlotOptions) {
            this.chartOptions.plotOptions = this.chartPlotOptions;
        }

        // chart scroll bar options
        if (this.chartScrollBarOptions) {
            this.chartOptions.scrollbar = this.chartScrollBarOptions;
        }

        // chart id
        if (this.chartId) {
            this.chartOptions.chart.renderTo = this.chartId as string;
        }

        // chart events
        if (this.chartEvents) {
            this.chartOptions.chart.events = this.chartEvents;
        }
        // chart legend
        if (this.showCustomLegend) {
            this.chartOptions.legend = this.chartCustomLegend;
        } else if (this.chartLegend) {
            this.chartOptions.legend = {
                enabled: this.chartLegend.enabled || false,
                layout: this.chartLegend.layout as Highcharts.OptionsLayoutValue || 'horizontal',
                align: this.chartLegend.align as Highcharts.AlignValue || 'right',
                verticalAlign: this.chartLegend.verticalAlign as Highcharts.VerticalAlignValue || 'bottom',
            };
        } else {
            this.chartOptions.legend = {
                enabled: true,
                layout: 'horizontal',
                align: 'right',
                verticalAlign: 'bottom',
            };
        }
        Highcharts.setOptions({
            time: {
                useUTC: false,
            },
        });
        if (this.chartElementRef && this.chartSeriesData) {
            this.chartElement = this.chartObject = new Highcharts.stockChart(this.chartOptions);
        }

        this.isLoading = false;
        // this.uiUtilService.safeChangeDetection(this.cdr);
    }

    public reloadChart() {
        this.renderChart();
    }

    public getWidth(): number {
        if (self.innerWidth) {
            return self.innerWidth;
        }
        if (this.el.nativeElement && this.el.nativeElement.offsetWidth) {
            return this.el.nativeElement.offsetWidth;
        }
        return 0;
    }

    public redrawChart() {
        if (this.chartObject) {
            this.chartObject.reflow();
        }
        this.renderChart();
    }

}
